
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Customer } from '../types';
import { customerApi as api } from '../utils/apiClient';

interface CustomerAuthContextType {
  customer: Customer | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

export const CustomerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const mapCustomerData = (data: any): Customer | null => {
    if (!data) return data;
    return {
      ...data,
      firstName: data.firstName || data.first_name || '',
      lastName: data.lastName || data.last_name || '',
      totalBookings: data.totalBookings || data.total_bookings || 0,
      totalSpent: data.totalSpent || data.total_spent || 0,
      lastBookingDate: data.lastBookingDate || data.last_booking_date || '',
      identity: data.identity || data.id
    };
  };

  const [customer, setCustomer] = useState<Customer | null>(() => {
    try {
      const cached = sessionStorage.getItem('es_cached_customer');
      if (!cached) return null;
      return mapCustomerData(JSON.parse(cached));
    } catch { return null; }
  });
  const [isLoading, setIsLoading] = useState(() => {
    try { return !sessionStorage.getItem('es_cached_customer'); } catch { return true; }
  });

  const cacheCustomer = (c: Customer | null) => {
    try {
      if (c) sessionStorage.setItem('es_cached_customer', JSON.stringify(c));
      else sessionStorage.removeItem('es_cached_customer');
    } catch { /* sessionStorage unavailable */ }
  };

  useEffect(() => {
    const checkSession = async () => {
      setIsLoading(true);
      try {
        const { data: { session } } = await api.auth.getSession();
        if (session) {
          await fetchCustomerProfile();
        }
      } catch (err) {
        console.warn("Session check failed:", err);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);



  const fetchCustomerProfile = async () => {
    try {
      const { data, error } = await api.from('customers').select('*').single();
      if (error) throw error;
      if (data) {
        const mappedData = mapCustomerData(data);
        setCustomer(mappedData);
        cacheCustomer(mappedData);
      }
    } catch (err) {
      console.error("Customer profile error:", err);
    }
  };

  const login = async (email: string, password: string) => {
    const { error } = await api.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message || error };

    await fetchCustomerProfile();
    return { success: true };
  };

  const logout = async () => {
    await api.auth.signOut();
    setCustomer(null);
    cacheCustomer(null);
  };

  return (
    <CustomerAuthContext.Provider value={{
      customer,
      isLoading,
      login,
      logout,
      isAuthenticated: !!customer
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  return context;
};


